import {
  Component, ChangeDetectionStrategy, ChangeDetectorRef, ViewChild, NgZone, Input, ElementRef, OnDestroy
} from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { NGXLogger } from 'ngx-logger';
import { ModalWindowComponent } from '../../../tools/modal-window/modal-window.component';
import { Appeaz } from '../../../tools/models/appeaz';
import { Asset } from '../../../tools/models/asset';
import { DynamicModification } from '../../../tools/models/dynamic-modification';
import { Step } from '../../../tools/models/step';
import { DynamicModificationService } from '../../../tools/services/dynamic-modification.service';
import { AlertType, EazlyMessage, MessageCause } from '../../../tools/services/messaging/eazly-message';
import { MessagingService } from '../../../tools/services/messaging/messaging.service';
import { PaletteService } from '../../../tools/services/palette.service';
import { ViewerService } from '../../../tools/services/viewer.service';
import { AddDMAction } from '../../../tools/undo-redo-manager/actions/add-dm-action';
import { UndoRedoManagerService } from '../../../tools/undo-redo-manager/undo-redo-manager.service';
import { NavigatorComponent } from '../../navigator/navigator.component';
import { PythonApiService } from '../../viewer/python-api.service';

@Component({
  selector: 'app-assistant-dialog',
  templateUrl: './assistant-dialog.component.html',
  styleUrls: ['./assistant-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})

export class AssistantDialogComponent implements OnDestroy {
  @Input()
  stepBeingEdited: Step;

  @Input()
  myappeaz: Appeaz;

  @ViewChild(NavigatorComponent)
  navigatorComponent: NavigatorComponent;

  @ViewChild('fileInput') fileInput: ElementRef<HTMLInputElement>;

  @ViewChild(ModalWindowComponent, { static: true })
  private readonly modalWindowComponent: ModalWindowComponent;

  userCancelClickedPromise: Promise<string>;
  viewerService: ViewerService;
  paletteService: PaletteService;
  dynamicModificationService: DynamicModificationService;
  event;
  newData: any[] = [];
  assets: Asset[];
  lastLabels: string[] = [];
  dropdownStates: { isOpen: boolean; selectedAsset: any; checked: boolean }[];
  showSubmitButtonErrorMessage: boolean[] = [];
  showImportTypeErrorMessage: boolean[] = [];
  isAssetSelected: boolean[] = [];
  showImportFieldErrorMessage: boolean[] = [];
  importedFileContentAsJson: any;
  shouldBeGrayedOut: boolean[] = [];
  rank: number;
  rankFromViewer: number;
  showImage: boolean[] = [];
  selectedImageData: string;
  isOpen: boolean;
  checked: boolean;
  isButtonDisabled: boolean;
  listDynamicModifs: Array<DynamicModification> = [];
  appeazUrl: string;
  step: Step;
  existingFields: any;
  modificationsToImport: any;
  public data: any;
  intersectionStep: Step[] = [];
  appeaz: Appeaz;
  loadingFinished: boolean;
  selectedFile: File | null = null;
  isCancelled: boolean;
  shadowPlaceholder: HTMLDivElement;
  resolutionUserCancelClickedPromise: () => void;
  eventListeners: any;
  errorReport: EazlyMessage;
  private eventHandler: () => void;


  constructor(viewerService: ViewerService,
    private readonly angularZone: NgZone,
    private readonly cdr: ChangeDetectorRef,
    private readonly sanitizer: DomSanitizer,
    paletteService: PaletteService,
    private readonly pythonApiService: PythonApiService,
    dynamicModificationService: DynamicModificationService,
    private readonly logger: NGXLogger,
    private readonly messagingService: MessagingService,
    private readonly undoRedoManagerService: UndoRedoManagerService,
  ) {
    this.viewerService = viewerService;
    this.paletteService = paletteService;
    this.dynamicModificationService = dynamicModificationService;
  }

  onSubmit(formData: any) {
    this.addToDynamicModifsList(formData);
    const addDmAction: AddDMAction = new AddDMAction(
      this.viewerService,
      this.appeaz.id,
      this.appeazUrl,
      this.appeaz.role,
      this.step.id,
      this.listDynamicModifs,
      this.rankFromViewer,
      undefined, true
    );
    this.undoRedoManagerService.do(addDmAction);
    this.hide();
  }

  public hide(): void {
    this.isCancelled = true;
    this.resolutionUserCancelClickedPromise();
    this.cancelLoading();
    this.modalWindowComponent.hide();
  }

  addToDynamicModifsList(formData: any) {
    this.listDynamicModifs = [];
    formData.forEach(async (data, index) => {
      if (this.dropdownStates[index].selectedAsset != null && this.dropdownStates[index].checked) {
        const fieldType = this.dropdownStates[index].selectedAsset['id'];
        const libelle = this.lastLabels[index];
        const dynamicModifProperties = this.dynamicModificationService.createDynamicModificationProperties(fieldType);
        dynamicModifProperties.editable = 'true';
        dynamicModifProperties.required = 'false';
        const modificationTarget = this.dynamicModificationService.createDynamicModificationTargetWithoutDataDescriptor(libelle, dynamicModifProperties);
        const dynamicModif = this.dynamicModificationService.createDynamicModification(undefined, this.event.target.id, this.rank, modificationTarget, 'ELEMENT_ADDING');
        this.rank++;
        this.listDynamicModifs.push(dynamicModif);
      }

    });

  }

  /**
  * This method initialize some attributes of assistant dialog.
  * 
  * @param event - The drop event from the viewer component.
  * 
  *  @param rank - The position whe we will import the fields.
  * 
  *  @param shadowPlaceholder - A place holder for the field to import.
  */
  public initAssistantAttributes(event: any, rank: number) {
    // Programmatically triggers a click on the file input element
    // to open the file selection dialog
    this.fileInput.nativeElement.click();
    this.rankFromViewer = rank;
    this.event = event;
    this.eventHandler = () => {
      if (!this.fileInput.nativeElement.value) {
        this.cancelLoading();
        this.cdr.detectChanges();
      }
    };
    window.addEventListener('focus', this.eventHandler, { once: true });

  }



  ngOnDestroy() {
    // Removing event Listeners
    window.removeEventListener('focus', this.eventHandler);
  }

  onFileSelected(event: Event) {
    this.loadingFinished = false;
    const input = event.target as HTMLInputElement;
    const selectedFile = input.files?.[0];
    this.selectedFile = selectedFile;
    if (this.selectedFile) {
      this.isCancelled = false;
      this.modalWindowComponent.show();
      this.uploadAndOpenDialog(this.selectedFile, this.rankFromViewer);
    }
    input.value = '';
    this.cdr.detectChanges();
  }

  public cancelLoading() {
    this.pythonApiService.cancelRequest();
  }

  async uploadAndOpenDialog(selectedFile: File, rank) {
    const jsonText = await this.uploadFileFromPythonApi(selectedFile);
    this.returnResult(jsonText, rank);
  }

  async uploadFileFromPythonApi(selectedFile: File): Promise<any> {

    // Promise that resolves when the user cancels the operation
    this.userCancelClickedPromise = new Promise<string>((resolve) => {
      this.resolutionUserCancelClickedPromise = () => {
        resolve('user-cancel');
      }
    });
    // Promise that resolves after 40 seconds (timeout)
    const timeoutPromise = new Promise<string>((resolve) => {
      setTimeout(() => resolve('timeout'), 40000);
    });

    try {
      // Start the request to upload the file and get the response
      const requestGanamPromise = this.pythonApiService.uploadFileAndGetResponse(selectedFile).toPromise();

      // Use Promise.race to wait for the first promise to resolve
      return await Promise.race([requestGanamPromise, timeoutPromise, this.userCancelClickedPromise]);

    } catch (error) {
      this.hide();
      this.handleErrors(error);
    }
  }


  public show(appeaz: Appeaz, stepBeingEdited: Step, data: any, rank): void {
    this.initAttributes(appeaz, stepBeingEdited, data, rank);
    this.hide();
    this.modalWindowComponent.show();
    this.cdr.detectChanges();
  }

  public initAttributes(appeaz: Appeaz, stepBeingEdited: Step, data: any, rank) {
    this.isCancelled = false;
    this.paletteService.getWizardAssets().subscribe(
      (assets) => (this.assets = assets)
    );
    this.step = stepBeingEdited;
    this.appeaz = appeaz;
    this.appeazUrl = appeaz.url;
    this.rank = rank;
    this.isButtonDisabled = false;
    this.data = data;
    this.newData = this.data.data;
    this.lastLabels = this.newData.map(item => item.name === '' ? 'Select a label' : item.name);
    this.showSubmitButtonErrorMessage = this.newData.map(item => false);
    this.showImportTypeErrorMessage = this.newData.map(item => false);
    this.isAssetSelected = this.newData.map(item => false);
    this.showImportFieldErrorMessage = this.newData.map(item => false);
    this.showImage = this.newData.map(item => false);
    this.shouldBeGrayedOut = this.newData.map(item => item.type === 'submit_button' || item.type === '');
    this.dropdownStates = this.newData.map(() => ({
      isOpen: false,
      selectedAsset: null,
      checked: true
    }));

  }

  handleErrors(error) {
    if (error.name === 'HttpErrorResponse') {
      this.cancelLoading();
      this.errorReport = new EazlyMessage(AlertType.danger,
        'PythonApiService', MessageCause.ImportInvalidPhotoMessage, '');
      this.messagingService.notifyError(this.errorReport);
    }
    else if (error.status === 500) {
      this.logger.debug('Error saving file to disk', error);
      this.errorReport = new EazlyMessage(AlertType.danger,
        'PythonApiService', MessageCause.ImportDiskMemoryErrorMessage, '');
      this.messagingService.notifyError(this.errorReport);
    }
    else {
      this.logger.debug('Unknown error:', error);
    }
  }

  /**
  * This method takes a string containing base64 encoded data
  * and converts it into a secure resource URL (SafeResourceUrl) that can be used in
  * contexts where Angular enforces strict security policies (e.g., in [src] attributes of images).
  * 
  * @param data - The base64 encoded string representing the image.
  * 
  * @returns A SafeResourceUrl object containing the secure resource URL.
  * 
  * Note: The 'image/png' format is used by default for images.
  */
  trustBase64(data: string): SafeResourceUrl {
    return this.sanitizer.bypassSecurityTrustResourceUrl(`data:image/png;base64,${data}`); // Adjust 'image/png' for the image format
  }

  checkDropdown(index: number): void {
    this.dropdownStates[index].isOpen = !this.dropdownStates[index].isOpen;
  }

  selectAsset(index: number, asset: any): void {
    this.dropdownStates[index].selectedAsset = asset;
    this.dropdownStates[index].isOpen = false;
    this.isAssetSelected[index] = true;

    if (this.showImportTypeErrorMessage[index]) {
      this.showImportTypeErrorMessage[index] = false;
    }
    else if (this.showImportFieldErrorMessage[index]) {
      this.showImportFieldErrorMessage[index] = false;
    }
    if (this.showSubmitButtonErrorMessage[index]) {
      this.showSubmitButtonErrorMessage[index] = false;
    }
    this.shouldBeGrayedOut[index] = false;

    if (this.isButtonDisabled && this.dropdownStates[index].checked) {
      this.isButtonDisabled = !this.isButtonDisabled;
    }

  }

  addTextAsset(index: number) {
    this.addAsset(index, 'TEXT_FIELD');
  }

  addChoiceAsset(index: number) {
    this.addAsset(index, 'COMBO_BOX');
  }

  addRadioAsset(index: number) {
    this.addAsset(index, 'RADIO_BUTTON');
  }

  addAsset(index: number, type: string) {
    this.dropdownStates[index].selectedAsset = this.assets.find(asset => asset.id === type);
  }

  onLabelTextChange(event: Event, index: number): void {
    this.lastLabels[index] = (event.target as HTMLDivElement).textContent;
    this.cdr.detectChanges();
  }

  areAllFalse(arr: boolean[]): boolean {
    return arr.every(element => element === false);
  }

  hasErrors(): boolean {
    const isShowImportTypeErrorMessagesAllFalse = this.areAllFalse(this.showImportTypeErrorMessage);
    const isShowImportFieldErrorMessagesAllFalse = this.areAllFalse(this.showImportFieldErrorMessage);
    const isShowSubmitButtonErrorMessagesAllFalse = this.areAllFalse(this.showSubmitButtonErrorMessage);
    return (!isShowImportTypeErrorMessagesAllFalse || !isShowImportFieldErrorMessagesAllFalse || !isShowSubmitButtonErrorMessagesAllFalse);
  }

  hasError(index: number): boolean {
    return (this.showSubmitButtonErrorMessage[index] || (this.showImportFieldErrorMessage[index] || this.showImportTypeErrorMessage[index]));
  }

  showError(index: number) {
    if (!this.showSubmitButtonErrorMessage[index] && !this.isAssetSelected[index]) {
      this.angularZone.run(() => {
        this.showSubmitButtonErrorMessage[index] = true;
        this.cdr.detectChanges();
      });
    }
  }

  showImportFieldError(index: number) {
    if (!this.showImportFieldErrorMessage[index] && !this.isAssetSelected[index]) {
      this.angularZone.run(() => {
        this.showImportFieldErrorMessage[index] = true;
        this.cdr.detectChanges();
      });
    }
  }

  showImportTypeError(index: number) {
    if (!this.showImportTypeErrorMessage[index] && !this.isAssetSelected[index]) {
      this.angularZone.run(() => {
        this.showImportTypeErrorMessage[index] = true;
        this.cdr.detectChanges();
      });
    }
  }

  changeCheckboxState(index: number, event: Event) {
    const checkbox = event.target as HTMLInputElement;
    const isChecked = checkbox.checked;
    this.dropdownStates[index].checked = isChecked;
    this.shouldBeGrayedOut[index] = !isChecked;

    const dataToBeImported: boolean[] = [];
    this.newData.forEach(async (data, index) => {
      if (this.dropdownStates[index].checked && !(this.showImportTypeErrorMessage[index] || this.showImportFieldErrorMessage[index] || this.showSubmitButtonErrorMessage[index])) {
        dataToBeImported.push(true);
      }
    });
    if (dataToBeImported.length > 0) {
      this.isButtonDisabled = false;
    } else {
      this.isButtonDisabled = true;
    }

  }

  initcheckboxState(id: number) {
    return !(this.showImportTypeErrorMessage[id] || this.showImportFieldErrorMessage[id] || this.showSubmitButtonErrorMessage[id]);
  }

  public returnResult(jsonText: any, rank: any) {
    if (jsonText === 'timeout') {
      this.logger.debug('API call unsuccessful after 40 seconds due to timeout.');
      this.hide();
      const errorReport = new EazlyMessage(AlertType.danger,
        'PythonApiService', MessageCause.ImportServorErrorMessage, '');
      this.messagingService.notifyError(errorReport);
    }
    else if (jsonText === 'user-cancel') {
      this.logger.debug('Task was cancelled by the user before receiving a response from the API call to PythonService');
    }
    else if (jsonText['data']) {
      this.loadingFinished = true;
      this.show(this.myappeaz, this.stepBeingEdited, jsonText, rank);
    }
  }

}
